import { Injectable, Injector } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AnnouncementOverlayComponent } from 'src/app/pages/announcement-overlay/announcement-overlay.component';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { BehaviorSubject } from 'rxjs';

export interface AnnouncementItem {
  title: string;
  content: SafeHtml | string;
  youtubeUrl?: SafeResourceUrl;
  driveUrl?: SafeResourceUrl;
}

@Injectable({ providedIn: 'root' })
export class AnnouncementOverlayService {

  readonly ANNOUNCEMENT_VERSION = 'v1';
  private overlayRef?: OverlayRef;

  private announcementsSubject =
    new BehaviorSubject<AnnouncementItem[]>([]);

  announcements$ = this.announcementsSubject.asObservable();

  private hasAnnouncement = false;

  constructor(
    private overlay: Overlay,
    private injector: Injector,
    private sanitizer: DomSanitizer
  ) {}

  setAnnouncements(contents: { pageTitle: string; content: string }[]): void {

    if (!contents.length) {
      this.hasAnnouncement = false;
      this.announcementsSubject.next([]);
      return;
    }

    this.hasAnnouncement = true;

    const announcements = contents.map(item => {

      const announcement: AnnouncementItem = {
        title: item.pageTitle,
        content: item.content
      };

      if (item.pageTitle?.startsWith('VIDEO_YOUTUBE')) {
        announcement.youtubeUrl =
          this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://www.youtube.com/embed/${this.extractVideoId(item.content)}`
          );
      }
      if (item.pageTitle?.startsWith('VIDEO_DRIVE')) {
        announcement.driveUrl =
          this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://drive.google.com/file/d/${this.extractFileId(item.content)}/preview`
          );
      }

      return announcement;
    });
    this.announcementsSubject.next(announcements);
    this.tryShow();
  }

  private extractVideoId(url: string): string {
    if (!url) return '';
    if (url.includes('watch?v=')) {
      return url.split('watch?v=')[1].split('&')[0];
    }
    if (url.includes('youtu.be/')) {
      return url.split('youtu.be/')[1].split('?')[0];
    }
    return '';
  }

  private extractFileId(url: string): string {
    if (!url) return '';
    const match = url.match(/\/d\/([^/]+)/);
    return match?.[1] || '';
  }

  tryShow(): void {
    // const dismissedVersion =
    //   sessionStorage.getItem('announcementDismissed');

    // if (
    //   !this.hasAnnouncement ||
    //   dismissedVersion === this.ANNOUNCEMENT_VERSION
    // ) {
    //   return;
    // }

    this.show();
  }

  show(): void {
    if (this.overlayRef) return;

    const position = this.overlay.position()
      .global()
      .centerHorizontally()
      .centerVertically();

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      positionStrategy: position,
      scrollStrategy: this.overlay.scrollStrategies.block()
    });

    this.overlayRef.attach(
      new ComponentPortal(AnnouncementOverlayComponent)
    );

    this.overlayRef.backdropClick().subscribe(() => this.hide());
  }

  hide(): void {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
    sessionStorage.setItem(
      'announcementDismissed',
      this.ANNOUNCEMENT_VERSION
    );
  }
}

